import { toRaw } from 'vue'

export interface StateMeta {
  /** Sequência monotônica. Invalida respostas de chamadas superadas. */
  seq: number
  /** Valor original de `data`, para os resets. */
  initData: unknown
  /** `true` quando o estado foi criado por `createAsyncState`. */
  hasInitData: boolean
}

/**
 * `WeakMap` — a entrada morre junto com o estado, que morre junto com o
 * componente. Um `Map` em escopo de módulo viraria vazamento permanente.
 *
 * O bookkeeping fica fora do objeto reativo de propósito: nada disso deve
 * aparecer no template, no Devtools ou em serialização de estado.
 */
const registry = new WeakMap<object, StateMeta>()

export function registerMeta(state: object, initData: unknown): StateMeta {
  const meta: StateMeta = { seq: 0, initData, hasInitData: true }
  registry.set(state, meta)

  return meta
}

/**
 * Recupera os metadados, criando sob demanda.
 *
 * A criação preguiçosa permite operar sobre um estado criado fora daqui —
 * nesse caso sem `initData`, então `resetData` é ignorado.
 */
export function ensureMeta(state: object): StateMeta {
  let meta = registry.get(state)

  if (!meta) {
    meta = { seq: 0, initData: undefined, hasInitData: false }
    registry.set(state, meta)
  }

  return meta
}

/**
 * Clona o valor inicial antes de devolvê-lo ao estado.
 *
 * Sem isso, um `initData` mutável compartilhado (`createAsyncState(DEFAULTS)`)
 * faria todas as instâncias apontarem para o mesmo objeto.
 *
 * `toRaw` é raso, o que basta para os valores iniciais usados na prática
 * (`null`, `[]`, `{}`).
 */
export function cloneInitData<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value

  try {
    return structuredClone(toRaw(value)) as T
  } catch {
    return value
  }
}
